import { useEffect, useState } from 'react';
import Error from 'next/error';
import Head from 'next/head';
import Image from 'next/legacy/image';
import Link from 'next/link';
import dayjs from 'dayjs';
import { gql } from '@apollo/client';
import parse, { Element } from 'html-react-parser';
import { useRouter } from 'next/router';
import { client, getAllPostsWithSlug, getPostByURI } from '../../apollo-client';
import { useAuth0User } from '@hooks/useAuth0User';
import { useAccessToken } from '@hooks/useAccessToken';
import { fetchPost } from '@api-service/content';
import {
  AFFILIATE_WORDPRESS_CATEGORY,
  MEMBERS_ONLY_WORDPRESS_CATEGORY,
  EXCLUDED_PATHS,
  EVERGREEN_ARTICLE_SLUGS,
  PAID_MEMBERSHIP_LEVELS,
  MEMBERSHIPS
} from '@lib/constants';
import {
  getLegacyRedirectPath,
  getMarketingCopy,
  withoutMigratedSlugs
} from '@lib/sanity';
import type { MarketingCopy } from '@lib/sanity';
import {
  getAuthorNameAsAffiliateString,
  getLinkDomain,
  getTextFromDomNode,
  isEmbeddedVideo,
  isImage,
  isImageLink,
  isLinkToArticle,
  isMatchingLink,
  isOptimisationCandidate,
  isPaidMember,
  isParagraphImage,
  isParagraphWrappingIframe,
  isUserAuthenticated,
  getYoastHeadSEO,
  hasSufficientPrivileges
} from '@lib/utils';
import { H1 } from '@components/atoms/typography';
import IframeWrapper from '@components/iframe-wrapper';
import Layout from '@components/layout';
import Paywall from '@components/paywall';
import PaywallFixedBottomModal from '@components/paywall/PaywallFixedBottomModal';
import { useTriggerPopUpPaywall } from '@hooks/useTriggerPopUpPaywall';

type ArticlePostProps = {
  post: any;
  marketingCopy: MarketingCopy;
};

/**
 * A body image capped at three quarters of the screen height, matching the
 * Sanity articles, so a tall graphic fits a phone screen. The width and height
 * attributes keep its space reserved while it loads. A plain img because the
 * images are served unoptimised anyway: WP Engine 403s Vercel's optimiser on
 * cache-miss (FFH-1688).
 */
const WordPressBodyImage = ({
  src,
  alt,
  width,
  height
}: {
  src: string;
  alt?: string;
  width?: string;
  height?: string;
}) => (
  <img
    src={src}
    alt={alt ?? ''}
    width={width ? Number(width) : undefined}
    height={height ? Number(height) : undefined}
    loading="lazy"
    decoding="async"
    className="article-image mx-auto block h-auto max-h-[75svh] w-auto max-w-full object-contain"
  />
);

const ArticlePost = ({ post, marketingCopy }: ArticlePostProps) => {
  const router = useRouter();
  const { user } = useAuth0User();
  const { accessToken } = useAccessToken();
  const [postContent, setPostContent] = useState('');
  const [hasAuthorisedContent, setHasAuthorisedContent] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const hasPaidMembership = isPaidMember(user);
  const hasSufficientMembershipLevel = hasSufficientPrivileges(
    user,
    PAID_MEMBERSHIP_LEVELS[MEMBERSHIPS.STANDARD_MONTHLY_ID].LEVEL
  );
  const isAuthorisedToViewRestrictedPageContent =
    hasPaidMembership && hasSufficientMembershipLevel;
  const isEvergreenArticle = EVERGREEN_ARTICLE_SLUGS.includes(post?.slug);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const anchorTags = document.querySelectorAll('a');
      anchorTags.forEach((anchor) => {
        if (!anchor.hasAttribute('data-clevertap-listener')) {
          anchor.addEventListener('click', (event) => {
            event.preventDefault();

            setTimeout(() => {
              router.push(anchor.href);
            }, 100);
          });
          anchor.setAttribute('data-clevertap-listener', 'true');
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      observer.disconnect();
    };
  }, [post]);

  useEffect(() => {
    if (!post) return;
    updatePostContent(post.content);
    if (hasSufficientMembershipLevel && !hasAuthorisedContent && accessToken) {
      loadAuthorisedContent();
    }
  }, [user, accessToken]);

  useTriggerPopUpPaywall({
    user,
    isAuthorisedToViewRestrictedPageContent,
    setShowModal
  });

  if (!post) {
    return <Error statusCode={404} />;
  }

  const yoastHead = getYoastHeadSEO(post?.seo?.fullHead, `/${post.slug}`);

  const updatePostContent = async (itemContent) => {
    let postContent = itemContent.toString();

    postContent = postContent.replace(
      /\r\n<h3>/g,
      '<p style="margin:0;padding:0;">&nbsp;</p><h3>'
    );
    const finalContent = postContent.replace(
      /\r\n<ul>/g,
      '<p style="margin:0;padding:0;">&nbsp;</p><ul>'
    );

    setPostContent(finalContent);
  };

  const loadAuthorisedContent = async () => {
    if (!accessToken) return;

    try {
      const rendered = await fetchPost(post.slug, accessToken);
      // undefined means not entitled (403) or no offloaded content (404);
      // leave the preview in place rather than blanking the page.
      if (rendered) {
        updatePostContent(rendered);
      }
    } catch (error) {
      console.error('[content] failed to load authorised post', error);
      updatePostContent(`
      <div style="padding: 2rem; text-align: center; background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; margin: 2rem 0;">
        <h3 style="color: #856404; margin-bottom: 1rem;">Unable to Load Article</h3>
        <p style="color: #856404; margin-bottom: 1.5rem;">We're having trouble loading this content. Please check your connection and try again.</p>
      </div>
    `);
    }

    setHasAuthorisedContent(true);
  };

  const author = post?.author;

  const isMembersArticle = post?.categories?.nodes?.filter(
    (category) => category.categoryId === MEMBERS_ONLY_WORDPRESS_CATEGORY
  ).length;

  const isAffiliateArticle = post?.categories?.nodes?.filter(
    (category) => category.categoryId === AFFILIATE_WORDPRESS_CATEGORY
  ).length;

  const getHeaderPaywallText = () => {
    if (post.paywallMessageText?.paywallHeader)
      return post.paywallMessageText?.paywallHeader;
    if (isMembersArticle) {
      return marketingCopy.paywall.title;
    }
    return '';
  };

  const getSubtitlePaywallText = () => {
    if (post.paywallMessageText?.paywallSubtitle)
      return post.paywallMessageText?.paywallSubtitle;
    if (isMembersArticle) {
      return marketingCopy.paywall.subtitle;
    }
    return '';
  };

  return (
    <>
      <Head>{yoastHead}</Head>
      <Layout>
        <div className="article">
          <div className="relative px-4 pt-8 pb-20 bg-white sm:px-6 lg:pb-10 lg:px-8">
            <div className="relative mx-auto max-w-7xl pb-14">
              <article className="article">
                <div className="text-center">
                  <H1 className="text-center">{parse(post.title)}</H1>
                  <small className="block mb-8 text-xs font-semibold tracking-wide text-center text-indigo-600 uppercase">
                    <Link
                      href={`${author.node.uri}`}
                      aria-label={`${author.node.uri}`}
                    >
                      {author.node.name}
                    </Link>
                    {!isEvergreenArticle && (
                      <>
                        {' | '}
                        {dayjs(post.date).format('ddd DD MMM YYYY')}
                      </>
                    )}
                  </small>
                </div>

                <picture className="mb-10">
                  <Image
                    className="featured-image w-full rounded-lg bg-gray-300"
                    src={
                      post.featuredImage?.node?.sourceUrl ??
                      '/images/hub-logo.svg'
                    }
                    objectFit="cover"
                    // The hero is locked to 16:9, so anything taller gets
                    // cropped. A centred crop cuts the top off players' heads,
                    // so bias the crop upward to protect head space (FFH-2345).
                    objectPosition="center top"
                    height={900}
                    width={1600}
                    priority
                    alt={post.title}
                    // WP Engine 403s Vercel's optimiser on cache-miss (FFH-1688)
                    unoptimized
                  />
                </picture>

                <div className="mt-3 text-base content-body text-black-400">
                  {parse(postContent, {
                    replace: (domNode: Element) => {
                      if (
                        domNode instanceof Element &&
                        domNode.attribs &&
                        domNode.attribs.class &&
                        domNode.attribs.class === 'epyt-video-wrapper'
                      ) {
                        // fixes double wrapping of iframe
                        domNode.attribs.class = '';

                        return { domNode };
                      }

                      if (isParagraphWrappingIframe(domNode)) {
                        const child = domNode.children?.[0] as Element;

                        return (
                          <IframeWrapper
                            id={child.attribs.id}
                            name={child.attribs.name}
                            src={child.attribs.src}
                            title={child.attribs.title}
                          />
                        );
                      }

                      if (isEmbeddedVideo(domNode)) {
                        return (
                          <IframeWrapper
                            id={domNode.attribs.id}
                            name={domNode.attribs.name}
                            src={domNode.attribs.src}
                            title={domNode.attribs.title}
                          />
                        );
                      }

                      if (isImage(domNode)) {
                        const height = domNode.attribs.height;
                        const width = domNode.attribs.width;
                        return (
                          <>
                            {height && isOptimisationCandidate(domNode) ? (
                              <WordPressBodyImage
                                src={domNode.attribs.src}
                                alt={domNode.attribs.alt}
                                width={width}
                                height={height}
                              />
                            ) : (
                              <img
                                alt={domNode.attribs.alt}
                                src={domNode.attribs.src}
                                className="mx-auto"
                              />
                            )}
                          </>
                        );
                      }

                      if (isParagraphImage(domNode)) {
                        const img = domNode.children?.[0] as Element;
                        const height = img.attribs.height;
                        const width = img.attribs.width;
                        return (
                          <div>
                            {height && isOptimisationCandidate(img) ? (
                              <WordPressBodyImage
                                src={img.attribs.src}
                                alt={img.attribs.alt}
                                width={width}
                                height={height}
                              />
                            ) : (
                              <img
                                alt={img.attribs.alt}
                                src={img.attribs.src}
                                className="mx-auto"
                              />
                            )}
                          </div>
                        );
                      }

                      if (isImageLink(domNode)) {
                        const img = domNode.children?.[0] as Element;
                        const height = img.attribs.height;
                        const width = img.attribs.width;
                        return (
                          <div className="mb-10">
                            <Link
                              href={domNode.attribs.href.replace(
                                process.env
                                  .NEXT_PUBLIC_MEMBERPRESS_BASE_URL as string,
                                `${getLinkDomain(window.location.origin)}`
                              )}
                              aria-label={domNode.attribs.href.replace(
                                process.env
                                  .NEXT_PUBLIC_MEMBERPRESS_BASE_URL as string,
                                `${getLinkDomain(window.location.origin)}`
                              )}
                            >
                              {height && isOptimisationCandidate(img) ? (
                                <WordPressBodyImage
                                  src={img.attribs.src}
                                  alt={img.attribs.alt}
                                  width={width}
                                  height={height}
                                />
                              ) : (
                                <img
                                  alt={img.attribs.alt}
                                  src={img.attribs.src}
                                  className="mx-auto"
                                />
                              )}
                            </Link>
                          </div>
                        );
                      }

                      if (
                        domNode.attribs &&
                        domNode.attribs.class === 'ff_inline_message'
                      ) {
                        return <></>;
                      }

                      if (isMatchingLink(domNode, '/premium-membership')) {
                        domNode.attribs.href = `${getLinkDomain(
                          process.env.NEXT_PUBLIC_HOSTNAME
                        )}/join`;

                        return { domNode };
                      }

                      if (isMatchingLink(domNode, '/?post_type=post&p=11173')) {
                        domNode.attribs.href = `${getLinkDomain(
                          process.env.NEXT_PUBLIC_HOSTNAME
                        )}/premier-league-set-piece-takers`;

                        return { domNode };
                      }

                      if (isMatchingLink(domNode, '/membership-registration')) {
                        domNode.attribs.href = `${getLinkDomain(
                          process.env.NEXT_PUBLIC_HOSTNAME
                        )}/join`;

                        return { domNode };
                      }

                      if (isMatchingLink(domNode, '/freetrial')) {
                        domNode.attribs.href = `${getLinkDomain(
                          process.env.NEXT_PUBLIC_HOSTNAME
                        )}/join`;

                        return { domNode };
                      }

                      if (
                        isMatchingLink(
                          domNode,
                          '/fanteam-fantasy-football-2021-22-tips-the-ultimate-guide'
                        )
                      ) {
                        domNode.attribs.href = `${getLinkDomain(
                          process.env.NEXT_PUBLIC_HOSTNAME
                        )}/fanteam-fantasy-football-tips-the-ultimate-guide`;

                        return { domNode };
                      }

                      if (isLinkToArticle(domNode)) {
                        return (
                          <Link
                            href={domNode.attribs.href.replace(
                              process.env
                                .NEXT_PUBLIC_MEMBERPRESS_BASE_URL as string,
                              `${getLinkDomain(window.location.origin)}`
                            )}
                            aria-label={domNode.attribs.href.replace(
                              process.env
                                .NEXT_PUBLIC_MEMBERPRESS_BASE_URL as string,
                              `${getLinkDomain(window.location.origin)}`
                            )}
                          >
                            {getTextFromDomNode(domNode)}
                          </Link>
                        );
                      }
                    }
                  })}
                </div>
              </article>
              {!isUserAuthenticated(user) || !hasPaidMembership ? (
                <>
                  <Paywall
                    variant="embedded"
                    title={getHeaderPaywallText()}
                    subtitle={getSubtitlePaywallText()}
                    returnTo={`/${post.slug}`}
                    affiliate={
                      isAffiliateArticle &&
                      getAuthorNameAsAffiliateString(author.node.name)
                    }
                    ctaText={marketingCopy.paywall.buttonText}
                  />
                  <PaywallFixedBottomModal
                    show={showModal}
                    handleClose={() => {
                      setShowModal(false);
                    }}
                    returnTo={`/${post.slug}`}
                    affiliate={
                      isAffiliateArticle &&
                      getAuthorNameAsAffiliateString(author.node.name)
                    }
                  />
                </>
              ) : (
                ''
              )}
              {hasPaidMembership && !isAuthorisedToViewRestrictedPageContent ? (
                <>
                  <Paywall
                    variant="embedded"
                    title={'Upgrade to read this Premium article'}
                    subtitle={'-'}
                    returnTo={`/${post.slug}`}
                    affiliate={
                      isAffiliateArticle &&
                      getAuthorNameAsAffiliateString(author.node.name)
                    }
                    ctaText="Upgrade now"
                  />
                </>
              ) : (
                ''
              )}
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

// Bots probe root paths for files and feeds; none of these can be a post, so
// they never cost a Sanity read or a WordPress fetch (FFH-2868).
const INVALID_SLUG_PATTERN =
  /\.(php|asp|aspx|jsp|cgi|env|xml|sql|bak|old|log|ini|conf|yml|yaml|toml|sh|bat|exe|dll|bin|dat|tmp|swp|png|jpe?g|gif|svg|webp|ico|json|js|css|map|txt|html?)$/i;
const STATIC_PROBE_SLUGS = new Set(['feed', 'wp-admin', 'wp-login', 'xmlrpc']);

const isStaticProbe = (slug: string): boolean =>
  INVALID_SLUG_PATTERN.test(slug) || STATIC_PROBE_SLUGS.has(slug.toLowerCase());

export const getStaticProps = async ({ params }) => {
  if (isStaticProbe(params.slug)) {
    return {
      notFound: true
    };
  }

  console.info(`Building page: ${params.slug}`);

  // The redirect map lives in the CMS (FFH-2630): a migrated document's
  // legacySlug is enough. The map is one cached read per lambda per TTL, not
  // a query per request (FFH-2868); a miss or a Sanity error falls through to
  // WordPress as before.
  const migratedPath = await getLegacyRedirectPath(params.slug);
  if (migratedPath) {
    return {
      redirect: {
        destination: migratedPath,
        permanent: true
      },
      revalidate: 600
    };
  }

  let data;
  try {
    // network-only: the module-level Apollo client persists across ISR
    // revalidations in a warm lambda, so cache-first would pin this
    // query to whatever the instance fetched first.
    const result = await client.query({
      query: gql`
        ${getPostByURI(`${params.slug}`)}
      `,
      fetchPolicy: 'network-only'
    });
    data = result.data;
  } catch (error) {
    console.error(`Failed to fetch post for slug "${params.slug}":`, error);
    return {
      notFound: true,
      revalidate: 60
    };
  }

  const post = data.postBy;

  if (!post || !post.postId) {
    // A null postBy can be a publish race (the request beat WordPress or
    // Stellate propagation), so the 404 must expire like the error path
    // above; without a revalidate it is cached until the next deploy.
    return {
      notFound: true,
      revalidate: 60
    };
  }

  const marketingCopy = await getMarketingCopy();

  return {
    props: {
      post,
      marketingCopy
    },
    revalidate: 600
  };
};

export async function getStaticPaths() {
  const FIRST_NUMBER_OF_POSTS = 1000;
  const afterCursor = null;

  const { data } = await client.query({
    query: gql`
      ${getAllPostsWithSlug(FIRST_NUMBER_OF_POSTS, afterCursor)}
    `
  });

  const allPosts = data.posts;
  const wordpressSlugs: string[] = (allPosts?.edges ?? [])
    .map(({ node }) => node?.slug)
    .filter((slug) => slug && !EXCLUDED_PATHS.includes(slug));

  // A post that has moved to Sanity must not be prerendered: Next rejects a
  // redirect from getStaticProps at build time, and would fail the whole
  // build on the first migrated post in the list (FFH-2868). Left out here,
  // it is built on its first request instead, where the redirect is allowed.
  const slugsToPrerender = await withoutMigratedSlugs(wordpressSlugs);

  return {
    paths: slugsToPrerender.map((slug) => `/${slug}`),
    fallback: 'blocking'
  };
}

export default ArticlePost;
